import axios from "axios";
import { errorToast, successToast } from "@idwal/idwal-react-components";
import { SectionGradeUpdate } from "../types/inspection";
import { getHeaderToken } from "../utils/serviceHelper";

/**
 * Grading service (A2-9130) — reads and persists tech-reviewer section grades
 * via the inspections-service routes exposed on REACT_APP_IDWAL_INTERNAL_API.
 * These routes are gated by the GradingAuthorizer (TOKEN authoriser) so every
 * call must carry BOTH an Authorization: Bearer <Cognito ID token> header (the
 * authoriser reads its cognito:groups claim) and the x-api-key (defence-in-depth
 * ApiKeyRequired). A missing Authorization header returns 401 at the gateway.
 *   GET  /inspections/{id}/grading-sections  — gradeable sections for the type
 *   GET  /inspections/{id}/grades            — recorded grades
 *   POST /inspections/{id}/grades            — save grades
 * Grade values are on the stored 0–10 `point` scale (null = ungraded).
 */

/** Ids are matched lower-case platform-wide; route GUIDs (Jira links) are not. */
const normaliseInspectionId = (inspectionId: string) => inspectionId.toLowerCase();

const gradesUrl = (inspectionId: string) =>
    `${process.env.REACT_APP_IDWAL_INTERNAL_API}/inspections/${normaliseInspectionId(inspectionId)}/grades`;

const gradingSectionsUrl = (inspectionId: string) =>
    `${process.env.REACT_APP_IDWAL_INTERNAL_API}/inspections/${normaliseInspectionId(inspectionId)}/grading-sections`;

const requestHeaders = async () => ({
    ...(await getHeaderToken()),
    "x-api-key": process.env.REACT_APP_IDWAL_INTERNAL_API_KEY,
});

/**
 * Fetch the gradeable sections for the inspection's type — the template the
 * Grading page renders (blank/ungraded before any grade is entered). Returns
 * the raw payload, or null when none exist (404) or the request fails.
 */
export const getGradingSections = async (inspectionId: string) => {
    try {
        const response = await axios.get(gradingSectionsUrl(inspectionId), { headers: await requestHeaders() });
        return response.data ?? null;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        errorToast("Failed to retrieve grading sections.");
        return null;
    }
};

/**
 * Fetch the recorded grades for an inspection. Returns the raw payload, or null
 * when none exist yet (404) or the request fails.
 */
export const getInspectionGrades = async (inspectionId: string) => {
    try {
        const response = await axios.get(gradesUrl(inspectionId), { headers: await requestHeaders() });
        return response.data ?? null;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        errorToast("Failed to retrieve grades.");
        return null;
    }
};

/**
 * Persist section grades (upserted into InspectionVesselGrade). Returns true on
 * success, false on failure (a toast is shown either way).
 */
export const saveInspectionGrades = async (
    inspectionId: string,
    grades: SectionGradeUpdate[]
): Promise<boolean> => {
    const body = { grades };

    try {
        const response = await axios.post(gradesUrl(inspectionId), body, { headers: await requestHeaders() });

        if (response.status === 200) {
            successToast("Grades have been updated.");
            return true;
        }
    } catch (error) {
        errorToast("Failed to update grades.");
    }

    return false;
};
